"use client"

import { useState } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import type { Answers } from "@/lib/quiz/types"

interface Props {
  answers: Answers
  onSubmit: (patch: Partial<Answers>) => void
  isFocus?: boolean
}

/**
 * Yes/No for the MRI question. Yes advances to the dedicated
 * `mri_addons` step (inserted dynamically by `stepsFor()` when
 * `mri_interest === true`). No advances straight to insurance with
 * every add-on flag cleared. The follow-up tiles used to render
 * inline here — now they live on their own step (`MriAddonsStep`)
 * so the MRI question stays short and focused.
 */
export function MriStep({
  answers: _answers,
  onSubmit,
  isFocus = false,
}: Props) {
  const t = useTranslations("quiz")
  const [picked, setPicked] = useState<boolean | null>(null)
  const [isAdvancing, setIsAdvancing] = useState(false)

  const handleClick = (yes: boolean) => {
    if (isAdvancing) return
    setPicked(yes)
    setIsAdvancing(true)
    window.setTimeout(() => {
      if (yes) {
        onSubmit({ mri_interest: true })
      } else {
        onSubmit({
          mri_interest: false,
          brain_interest: false,
          calcium_interest: false,
          dexa_interest: false,
          mamma_interest: false,
        })
      }
    }, 220)
  }

  return (
    <div
      className={
        "flex flex-col gap-3 sm:flex-row" +
        (isFocus ? " sm:justify-center" : "")
      }
    >
      <button
        onClick={() => handleClick(true)}
        disabled={isAdvancing && picked !== true}
        className={
          "type-title-md cursor-pointer rounded-[12px] border px-6 py-4 transition-colors disabled:cursor-not-allowed sm:min-w-[140px] " +
          (picked === true
            ? "border-deep-blue-900 bg-deep-blue-900 text-white"
            : "hover:border-deep-blue-900 hover:bg-deep-blue-50 border-neutral-300 bg-white text-neutral-900 disabled:opacity-40")
        }
      >
        {t("yes")}
      </button>
      <button
        onClick={() => handleClick(false)}
        disabled={isAdvancing && picked !== false}
        className={
          "type-title-md cursor-pointer rounded-[12px] border px-6 py-4 transition-colors disabled:cursor-not-allowed sm:min-w-[140px] " +
          (picked === false
            ? "border-deep-blue-900 bg-deep-blue-900 text-white"
            : "hover:border-deep-blue-900 hover:bg-deep-blue-50 border-neutral-300 bg-white text-neutral-900 disabled:opacity-40")
        }
      >
        {t("no")}
      </button>
    </div>
  )
}
